import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "reactstrap";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { getUnitById } from "../../../../slices/thunks";
import LoadingState from "../shared/components/LoadingState";
import ErrorState from "../shared/components/ErrorState";

import C from "./constants/colors";
import { fmtCurrency } from "./utils/format";
import { isUnitSold, getPriceDifference, getPriceDiffPercent } from "./utils/unitCalculations";
import HeroHeader from "./components/HeroHeader";
import StatCard from "./components/StatCard";
import TechnicalSpecs from "./components/TechnicalSpecs";
import ContractOwnership from "./components/ContractOwnership";

import "./css/UnitDetail.css";

const UnitDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUnit = useCallback(() => {
    if (!id) return;
    setLoading(true);
    dispatch(getUnitById(id))
      .unwrap()
      .then((res) => { setData(res); setLoading(false); })
      .catch((err) => { setError(err.message || "Failed to load unit"); setLoading(false); });
  }, [id, dispatch]);

  useEffect(() => { fetchUnit(); }, [fetchUnit]);

  if (loading) return <LoadingState color={C.primary} />;
  if (error) return <ErrorState error={error} color={C.primary} onRetry={() => window.location.reload()} />;

  const { unit, block, project, contract, client } = data || {};
  const sold = isUnitSold(data);
  const currency = contract?.currency || "EUR";
  const priceDiff = getPriceDifference(data);
  const priceDiffPct = getPriceDiffPercent(data);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={`Unit ${unit?.nr || id}`} pageTitle={block?.name || "Units"} />

        <HeroHeader unit={unit} block={block} project={project} sold={sold} />

        <Row className="g-4 mb-4">
          <Col md={4}>
            <StatCard
              icon={priceDiff != null && priceDiff >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}
              label="Price Gain / Discount"
              value={sold && priceDiff != null ? `${priceDiff >= 0 ? "+" : ""}${fmtCurrency(priceDiff, currency)}` : "—"}
              valueColor={sold && priceDiff != null ? (priceDiff >= 0 ? C.success : C.danger) : C.primary}
              sub={
                sold && priceDiffPct != null
                  ? `${priceDiffPct >= 0 ? "+" : ""}${priceDiffPct}% ${priceDiff >= 0 ? "above" : "below"} listing`
                  : "vs. initial listing price"
              }
              subColor={sold && priceDiff != null ? (priceDiff >= 0 ? C.success : C.danger) : undefined}
            />
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col lg={4}>
            <TechnicalSpecs data={data} currency={currency} sold={sold} />
          </Col>
          <Col lg={8}>
            <ContractOwnership
              contract={contract}
              client={client}
              currency={currency}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UnitDetail;
